import React from 'react';
import { Box, Container, IconButton, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import FlexBox from '../FlexBox';
import { useEffect, useContext, useState } from 'react';
import { GlobalContext } from 'context/GlobalState';
import { currency, routerUrls, paymentTypes, bankDetails } from 'constants';
import { strings } from 'localisation';
import ScrollToTop from '../ScrollToTop';
import SocialLinks from '../SocialLinks';
import { getTuckAllowanceNote, hasTuckAllowance } from '../../utils/heightUtils';
import createGAevent from 'helpers';

import { Helmet } from 'react-helmet';
import { cartLineKey } from '../../utils/cartUtils';
import { copyToClipboard } from '../../utils/clipboard';

const toEUR = (amount) => parseFloat(amount || 0).toFixed(2);

// The currency code is a unit, not part of the figure, so it reads a step down from the amount.
// Same hierarchy as the checkout summary the customer just came from.
const currencyCodeSx = { fontSize: '0.8rem', fontWeight: 500, letterSpacing: '0.04em' };

/** An EUR amount matching the checkout summary the customer just left. */
const EuroPrice = ({ amount }) => (
  <React.Fragment>
    {toEUR(amount)}{' '}
    <Box component="span" sx={currencyCodeSx}>
      {currency.EUR}
    </Box>
  </React.Fragment>
);

EuroPrice.propTypes = {
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

/** A label and its figure, as two cells of the parent grid so the amounts share a column. */
const SummaryRow = ({ label, amount, emphasis = false }) => (
  <React.Fragment>
    <Typography
      color="text.primary"
      sx={
        emphasis
          ? { fontSize: 12, fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase' }
          : undefined
      }>
      {label}
    </Typography>
    <Typography
      component="span"
      color="price.main"
      sx={{ justifySelf: 'end', whiteSpace: 'nowrap', fontWeight: 600, lineHeight: 1.1 }}>
      <EuroPrice amount={amount} />
    </Typography>
  </React.Fragment>
);

SummaryRow.propTypes = {
  label: PropTypes.string.isRequired,
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  emphasis: PropTypes.bool
};

/** One fact about the order: a gold eyebrow over the value. */
const MetaItem = ({ label, value }) => (
  <Box>
    <Typography variant="overline" color="gold.text" component="div">
      {label}
    </Typography>
    <Typography color="text.primary" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);

MetaItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired
};

// index.css turns selection off site-wide to protect the images, which would leave the customer
// unable to copy the IBAN into their banking app. Give it back on the values they must copy.
const selectableSx = { userSelect: 'text', WebkitUserSelect: 'text' };

/**
 * Copies one value and confirms it in place - the tick replaces the icon for a moment rather
 * than raising a toast, so the customer's eye stays on the field they just took.
 */
const CopyButton = ({ value, label }) => {
  const { lang } = useContext(GlobalContext);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(timer);
  }, [copied]);

  // A failed copy leaves the icon alone: the value is selectable, so there is still a way through.
  const handleClick = async () => {
    if (await copyToClipboard(value)) setCopied(true);
  };

  const title = `${copied ? strings[lang].copied : strings[lang].copy}: ${label}`;

  return (
    <IconButton
      size="small"
      onClick={handleClick}
      title={title}
      aria-label={title}
      sx={{
        p: 0.5,
        flexShrink: 0,
        color: copied ? 'success.main' : 'gold.text',
        '&:hover': { backgroundColor: 'gold.soft' }
      }}>
      {copied ? (
        <CheckRoundedIcon sx={{ fontSize: 16 }} />
      ) : (
        <ContentCopyRoundedIcon sx={{ fontSize: 15 }} />
      )}
    </IconButton>
  );
};

CopyButton.propTypes = {
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired
};

/**
 * A label and its value, as two cells of the bank-details grid so the values share a column.
 * `copyValue` is the string the customer retypes into their banking app: passing it both shows
 * the value and puts a copy button beside it. Rows that are only there to be read pass children.
 */
const BankRow = ({ label, copyValue, children }) => (
  <React.Fragment>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        minWidth: 0,
        // Stacked on a phone, so the gap that separates one pair from the next has to sit under
        // the value - an even row gap would leave every label floating between two values.
        mb: { xs: 2, sm: 0 }
      }}>
      <Typography
        color="text.primary"
        sx={{
          fontWeight: 600,
          wordBreak: 'break-word',
          fontVariantNumeric: 'tabular-nums',
          ...(copyValue ? selectableSx : null)
        }}>
        {children || copyValue}
      </Typography>
      {copyValue && <CopyButton value={copyValue} label={label} />}
    </Box>
  </React.Fragment>
);

BankRow.propTypes = {
  label: PropTypes.string.isRequired,
  copyValue: PropTypes.string,
  children: PropTypes.node
};

function padTo2Digits(num) {
  return num.toString().padStart(2, '0');
}

function formatDate(date) {
  return [padTo2Digits(date.getDate()), padTo2Digits(date.getMonth() + 1), date.getFullYear()].join(
    '/'
  );
}

export default function OrderPlaced() {
  const { lang, clearCartList } = useContext(GlobalContext);
  const { state } = useLocation();

  // The page is only reachable by navigating from checkout, which passes the order in router
  // state. A direct visit - refresh, bookmark, pasted link - has none, so send them home
  // rather than throw on `state.values`.
  const order = state?.values;

  useEffect(() => {
    if (!order) return;

    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      const orderTotalEur = parseFloat(order.total) || 0;
      window.fbq('track', 'Purchase', {
        value: Number(orderTotalEur.toFixed(2)),
        currency: 'EUR',
        num_items: (order.cartList || []).reduce(
          (sum, item) => sum + (parseInt(item.quantity, 10) || 0),
          0
        ),
        contents: (order.cartList || []).map((item) => ({
          id: String(item.id != null ? item.id : item.slug_bg || item.name),
          quantity: parseInt(item.quantity, 10) || 0,
          item_price: Number((parseFloat(item.price) || 0).toFixed(2))
        })),
        content_type: 'product'
      });
    } else {
      console.warn('fbq not available in Purchase');
    }
    clearCartList();
    createGAevent(
      'OrderPlaced',
      'ORDER_COMPLETED',
      'orderId-' + order.uuidOrder + '---orderTotal-' + order.total
    );

    createGAevent(
      'OrderPlaced',
      `ORDER_COMPLETED-orderId-${order.uuidOrder}-orderTotal-${order.total}`,
      'orderId-' + order.uuidOrder + '---orderTotal-' + order.total
    );
  }, []);

  if (!order) return <Navigate to={routerUrls.root} replace />;

  const currentDate = formatDate(new Date());
  const { paymentType, uuidOrder, shippingType, shippingPrice, subtotal, total, note } = order;
  const isBankTransfer = paymentType === paymentTypes.bankTransfer;

  const lineTotalEUR = (price, quantity) =>
    (parseFloat(price || 0) * parseFloat(quantity || 0)).toFixed(2);

  return (
    <ScrollToTop>
      <Helmet>
        <title> Поръчката е получена</title>
        <meta name="robots" content="noindex, follow" />
        <meta name="description" content="поръчката е получена" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <Container maxWidth="md" sx={{ pt: 6, pb: 8 }}>
        {/* The tick and the two lines are the whole message. Everything below is the receipt. */}
        <FlexBox flexDirection="column" alignItems="center" textAlign="center" mb={5}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              backgroundColor: 'gold.soft',
              border: '1px solid',
              borderColor: 'gold.fill',
              mb: 3
            }}>
            <CheckRoundedIcon sx={{ fontSize: 30, color: 'gold.text' }} />
          </Box>

          <Typography variant="h3" color="text.primary" mb={1.5}>
            {strings[lang].thankYou}
          </Typography>
          <Typography color="text.secondary" sx={{ letterSpacing: '0.02em' }}>
            {strings[lang].yourOrderPlaced}
          </Typography>
        </FlexBox>

        {/* Bank transfer is the one payment method that still asks something of the customer, so
            the account details sit above the receipt rather than buried inside it. */}
        {isBankTransfer && (
          <Box
            sx={{
              mb: 5,
              px: { xs: 2.5, sm: 3.5 },
              py: 3,
              border: '1px solid',
              borderColor: 'gold.fill',
              backgroundColor: 'gold.soft'
            }}>
            <Typography variant="overline" color="gold.text" component="div">
              {strings[lang].bankTransferTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2.5}>
              {strings[lang].bankTransferIntro}
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 11rem) 1fr' },
                columnGap: 2,
                rowGap: { xs: 0, sm: 1.5 },
                alignItems: 'baseline'
              }}>
              <BankRow label={strings[lang].bankTransferAmount}>
                <EuroPrice amount={total} />
              </BankRow>
              <BankRow label="IBAN" copyValue={bankDetails.iban} />
              <BankRow label="BIC" copyValue={bankDetails.bic} />
              <BankRow label={strings[lang].bankTransferBank}>{bankDetails.bank}</BankRow>
              <BankRow label={strings[lang].bankTransferHolder} copyValue={bankDetails.holder} />
              {uuidOrder && (
                <BankRow label={strings[lang].bankTransferReference} copyValue={uuidOrder} />
              )}
            </Box>
          </Box>
        )}

        {/* The facts a customer screenshots or quotes back to us. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 3,
            px: 3,
            py: 2.5,
            mb: 5,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper'
          }}>
          {uuidOrder && <MetaItem label={strings[lang].orderNumber} value={uuidOrder} />}
          <MetaItem label={strings[lang].date} value={currentDate} />
          {paymentType && <MetaItem label={strings[lang].paymentMethodLabel} value={paymentType} />}
        </Box>

        {/* House rules the customer needs after ordering, not before. */}
        <Box sx={{ borderLeft: '2px solid', borderColor: 'gold.fill', pl: 2.5, mb: 5 }}>
          <Typography variant="overline" color="gold.text" component="div" mb={0.5}>
            {strings[lang].reminderTitle}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            component="div"
            sx={{
              '& p': { m: 0, mb: 1.5 },
              '& p:last-of-type': { mb: 0 },
              '& strong': { color: 'text.primary', fontWeight: 600 }
            }}
            dangerouslySetInnerHTML={{ __html: strings[lang].reminderMessage }}
          />
        </Box>

        <Typography
          variant="overline"
          color="text.secondary"
          component="div"
          sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
          {strings[lang].orderDetails}
        </Typography>

        {order.cartList?.map((item) => (
          <FlexBox alignItems="flex-start" gap={2} mb={3} key={cartLineKey(item)}>
            <Box
              component="img"
              src={item.imgUrl || '/assets/images/products/iphone-xi.png'}
              alt={item.name}
              sx={{
                display: 'block',
                width: 96,
                height: 96,
                objectFit: 'cover',
                flexShrink: 0,
                border: '1px solid',
                borderColor: 'divider'
              }}
            />

            <Box minWidth={0} width="100%">
              <Typography color="text.primary" sx={{ fontWeight: 600 }}>
                {item.name}
              </Typography>

              {item.attributes &&
                Object.keys(item.attributes).map((attribute) => (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    key={item.attributes[attribute].id}>
                    {item.attributes[attribute].label} : {item.attributes[attribute].value} cm
                  </Typography>
                ))}

              {hasTuckAllowance(item) && (
                <Typography variant="body2" color="text.secondary">
                  {getTuckAllowanceNote(lang)}
                </Typography>
              )}

              <FlexBox
                justifyContent="space-between"
                alignItems="baseline"
                gap={2}
                flexWrap="wrap"
                mt={1}>
                <Typography variant="body2" color="text.secondary">
                  {toEUR(item.price)} {currency.EUR} × {item.quantity}
                </Typography>
                <Typography
                  color="price.main"
                  sx={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    fontVariantNumeric: 'tabular-nums'
                  }}>
                  <EuroPrice amount={lineTotalEUR(item.price, item.quantity)} />
                </Typography>
              </FlexBox>
            </Box>
          </FlexBox>
        ))}

        {/* The same card as the checkout summary, so the figures the customer approved
            reappear in the shape they approved them in. */}
        <Box
          sx={{
            px: 2.5,
            py: 2.25,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            columnGap: 1.25,
            rowGap: 1.25,
            alignItems: 'baseline',
            fontVariantNumeric: 'tabular-nums'
          }}>
          <SummaryRow label={strings[lang].subtotal} amount={subtotal} />
          <SummaryRow
            label={
              shippingType ? `${strings[lang].shipping} · ${shippingType}` : strings[lang].shipping
            }
            amount={shippingPrice}
          />

          {/* A gold rule, not a grey one: the total is the line the eye should land on. */}
          <Box
            sx={{
              gridColumn: '1 / -1',
              height: '1px',
              backgroundColor: 'gold.fill',
              opacity: 0.45,
              my: 0.75
            }}
          />

          <SummaryRow emphasis label={strings[lang].total} amount={total} />
        </Box>

        {note && (
          <Box mt={4}>
            <Typography variant="overline" color="text.secondary" component="div">
              {strings[lang].note}
            </Typography>
            <Typography color="text.primary">{note}</Typography>
          </Box>
        )}

        {/* The receipt is finished; the only thing left to offer is a reason to come back. */}
        <Box
          sx={{
            mt: 6,
            pt: 4,
            borderTop: '1px solid',
            borderColor: 'divider',
            textAlign: 'center'
          }}>
          <Typography
            variant="overline"
            color="gold.text"
            component="div"
            mb={2}
            sx={{ fontSize: '1.1rem', letterSpacing: '0.09em' }}>
            {strings[lang].followUs}
          </Typography>
          <SocialLinks justifyContent="center" size="large" />
        </Box>
      </Container>
    </ScrollToTop>
  );
}
